import { type Ref } from "react";
import clsx from "clsx";
import type { PlanungCalendarDay } from "@/lib/montageplanung";
import { isSameDay, isWeekend, toIso, WEEKDAY_SHORT } from "./dateUtils";
import { StickyMonthLabels, type MonthHeadGroup } from "./StickyMonthLabels";

type HeadGroup = { key: string; label: string; span: number; startIdx: number };

export interface PlanningHeadRowProps {
  labelW: number;
  totalWidth: number;
  dayW: number;
  months: HeadGroup[];
  weekGroups: HeadGroup[];
  days: Date[];
  calendarByDay: Map<string, PlanungCalendarDay>;
  today: Date;
  isCompact: boolean;
  headScrollLeft?: number;
  headTrackVisibleWidth?: number;
  trackClassName?: string;
  trackScrollRef?: Ref<HTMLDivElement>;
}

export function PlanningHeadTrack({
  totalWidth,
  dayW,
  months,
  weekGroups,
  days,
  calendarByDay,
  today,
  trackClassName,
}: Omit<
  PlanningHeadRowProps,
  "labelW" | "isCompact" | "headScrollLeft" | "headTrackVisibleWidth"
>) {
  return (
    <div
      className={clsx("mp-headtrack mp-head", trackClassName)}
      style={{ width: totalWidth }}
    >
      <div className="mp-head-months">
        {months.map((m) => (
          <div
            key={m.key}
            className="mp-head-month-cell"
            style={{ width: m.span * dayW }}
            aria-hidden
          />
        ))}
      </div>
      <div className="mp-head-weeks">
        {weekGroups.map((w) => (
          <div key={w.key} style={{ width: w.span * dayW }}>
            {w.label}
          </div>
        ))}
      </div>
      <div className="mp-head-days">
        {days.map((d) => {
          const iso = toIso(d);
          const isToday = isSameDay(d, today);
          const weekend = isWeekend(d);
          const calendarDay = calendarByDay.get(iso);
          const dayTitle =
            calendarDay?.label ?? (weekend ? "Wochenende (Sa/So)" : undefined);
          return (
            <div
              key={iso}
              style={{ width: dayW }}
              title={dayTitle}
              className={clsx(
                weekend && "mp-weekend",
                calendarDay?.kind === "holiday" && "mp-holiday",
                calendarDay?.kind === "bridge" && "mp-bridge",
                calendarDay?.kind === "betriebsferien" && "mp-betriebsferien",
                isToday && "mp-today mp-today-head",
              )}
            >
              <div className="mp-head-day-wd">{WEEKDAY_SHORT[d.getDay()]}</div>
              <div className="mp-head-day-num">{d.getDate()}</div>
              {calendarDay?.kind === "holiday" && (
                <div className="mp-head-day-tag mp-head-day-tag--holiday">FT</div>
              )}
              {calendarDay?.kind === "bridge" && (
                <div className="mp-head-day-tag mp-head-day-tag--bridge">B</div>
              )}
              {calendarDay?.kind === "betriebsferien" && (
                <div className="mp-head-day-tag mp-head-day-tag--betriebsferien">BF</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PlanningHeadRow({
  labelW,
  totalWidth,
  dayW,
  months,
  weekGroups,
  days,
  calendarByDay,
  today,
  isCompact,
  headScrollLeft = 0,
  headTrackVisibleWidth = 0,
  trackClassName,
  trackScrollRef,
}: PlanningHeadRowProps) {
  const monthGroups = months as MonthHeadGroup[];
  const monthLabels = (
    <StickyMonthLabels
      months={monthGroups}
      dayW={dayW}
      scrollLeft={headScrollLeft}
      trackVisibleWidth={headTrackVisibleWidth || totalWidth}
      today={today}
      viewportRelative
    />
  );

  const track = (
    <PlanningHeadTrack
      totalWidth={totalWidth}
      dayW={dayW}
      months={months}
      weekGroups={weekGroups}
      days={days}
      calendarByDay={calendarByDay}
      today={today}
      trackClassName={trackClassName}
    />
  );

  return (
    <>
      <div className="mp-label mp-corner" style={{ width: labelW }}>
        {isCompact ? (
          "Pers."
        ) : (
          <span className="mp-corner-stack">
            <span>Mitarbeiter</span>
            <span className="mp-corner-sub">Ressource</span>
          </span>
        )}
      </div>
      {trackScrollRef ? (
        <div className="mp-calendar-head-scroll">
          {monthLabels}
          <div ref={trackScrollRef} className="mp-calendar-head-scroll-inner">
            {track}
          </div>
        </div>
      ) : (
        <div className="mp-calendar-head-static">
          {monthLabels}
          {track}
        </div>
      )}
    </>
  );
}
